import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowBackIos } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import {
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';

import { DirectLoginFormWrapper } from './campus-card';

import { CAMPUS_CARD_AUTH_TYPE, PAYMENT_STEP, QUERY_KEY } from '~common';
import { LocalizationTypography } from '~components/organisms/localization';
import { useQueryGetInstitution, useQueryListInstitutions } from '~hooks';
import {
  useLanguageContext,
  usePaymentStepContext,
  useStationInformationContext,
  useUserPaymentContext,
} from '~providers';
import { CampusCardInstitution } from '~types';

const SKELETON_COUNT = 3;

export const AddCampusCard: FC = () => {
  const { lt } = useLanguageContext();
  const queryClient = useQueryClient();
  const { handleSetPaymentStep } = usePaymentStepContext();

  const [search, setSearch] = useState('');
  const [selectedInstitution, setSelectedInstitution] =
    useState<CampusCardInstitution | null>(null);

  const { campusCard } = useStationInformationContext();
  const { userCampusCards } = useUserPaymentContext();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useQueryListInstitutions(search);
  const institutions = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data?.pages],
  );

  const { data: supportedInstitution } = useQueryGetInstitution(
    campusCard.institutionId,
    { enabled: !search },
  );

  const linkedInstitutionUuids = useMemo(
    () => new Set(userCampusCards.map((card) => card.institutionUuid)),
    [userCampusCards],
  );

  const displayList = useMemo<CampusCardInstitution[]>(() => {
    if (search || !supportedInstitution) {
      return institutions;
    }
    return [
      supportedInstitution,
      ...institutions.filter((i) => i.uuid !== supportedInstitution.uuid),
    ];
  }, [search, supportedInstitution, institutions]);

  const getInstitutionStatus = useCallback(
    (uuid: string): 'linked' | 'not_supported' | null => {
      if (linkedInstitutionUuids.has(uuid)) {
        return 'linked';
      }
      if (
        campusCard.enabled &&
        campusCard.institutionId &&
        uuid !== campusCard.institutionId
      ) {
        return 'not_supported';
      }
      return null;
    },
    [campusCard.enabled, campusCard.institutionId, linkedInstitutionUuids],
  );

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearch(value);
  }, 500);

  // Infinite scroll: fetch the next page when the sentinel at the list end
  // scrolls into view.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleBack = useCallback(() => {
    if (selectedInstitution) {
      setSelectedInstitution(null);
      return;
    }
    handleSetPaymentStep(PAYMENT_STEP.CAMPUS_CARD_LIST);
  }, [handleSetPaymentStep, selectedInstitution]);

  const handleLinked = useCallback(async () => {
    // Section is unmounted here, and the QueryClient defaults disable
    // refetchOnMount + use a 2min staleTime. refetchType 'all' forces the
    // inactive list query to refetch now so the cache is fresh on remount.
    await queryClient.invalidateQueries([QUERY_KEY.GET_USER_PAYMENT_METHODS], {
      refetchType: 'all',
    });
    handleSetPaymentStep(PAYMENT_STEP.CAMPUS_CARD_LIST);
  }, [handleSetPaymentStep, queryClient]);

  return (
    <>
      <Stack direction="row">
        <IconButton
          aria-label="back"
          size="small"
          color="inherit"
          sx={{ paddingY: 0 }}
          onClick={handleBack}
        >
          <ArrowBackIos />
        </IconButton>
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.campusCard.addTitle"
          defaultText="Add Campus Card"
          variant="Poppins/Heading-3"
        />
      </Stack>

      {selectedInstitution ? (
        selectedInstitution.authType === CAMPUS_CARD_AUTH_TYPE.DIRECT ? (
          <DirectLoginFormWrapper
            institution={selectedInstitution}
            onLinked={handleLinked}
          />
        ) : (
          // SSO (auth_type 2) is deferred — pending data verification.
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.campusCard.ssoComingSoon"
            defaultText="This institution uses single sign-on, which isn't available yet. Please choose another institution."
            variant="Inter/Body-sm"
            color="text.secondary"
            sx={{ paddingY: 2 }}
          />
        )
      ) : (
        <Stack spacing={1}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={lt(
              'app.rentalPage.newStepThree.campusCard.searchPlaceholder',
              'Search your school',
            )}
            onChange={(e) => debouncedSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ color: (theme) => theme.palette.neutral[400] }}
                  />
                </InputAdornment>
              ),
            }}
            sx={(theme) => ({
              my: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: theme.palette.neutral[50],
                '& fieldset': { borderColor: theme.palette.neutral[200] },
                '&:hover fieldset': { borderColor: theme.palette.neutral[300] },
              },
              '& .MuiOutlinedInput-input': {
                paddingY: 1.25,
                '&::placeholder': {
                  color: theme.palette.neutral[400],
                  opacity: 1,
                },
              },
            })}
          />

          {isLoading ? (
            <>
              {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  height={48}
                  sx={{ my: 0.5 }}
                />
              ))}
            </>
          ) : displayList.length > 0 ? (
            <>
              {displayList.map((institution) => {
                const status = getInstitutionStatus(institution.uuid);
                const isDisabled = status !== null;
                const statusLabel =
                  status === 'linked'
                    ? lt(
                        'app.rentalPage.newStepThree.campusCard.linked',
                        'linked',
                      )
                    : status === 'not_supported'
                    ? lt(
                        'app.rentalPage.newStepThree.campusCard.notSupported',
                        'not supported',
                      )
                    : '';
                return (
                  <Stack
                    key={institution.uuid}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={
                      isDisabled
                        ? undefined
                        : () => setSelectedInstitution(institution)
                    }
                    sx={(theme) => ({
                      paddingX: 1.5,
                      paddingY: 1.5,
                      borderRadius: 1,
                      cursor: isDisabled ? 'default' : 'pointer',
                      bgcolor: isDisabled
                        ? theme.palette.neutral[100]
                        : 'transparent',
                      pointerEvents: isDisabled ? 'none' : 'auto',
                    })}
                  >
                    <Typography
                      variant="Poppins/Subheading-md"
                      color="text.primary"
                    >
                      {institution.name}
                    </Typography>
                    {statusLabel && (
                      <Typography
                        variant="Inter/Body-sm"
                        color="text.secondary"
                      >
                        {statusLabel}
                      </Typography>
                    )}
                  </Stack>
                );
              })}
              {isFetchingNextPage && (
                <Skeleton variant="rectangular" height={48} sx={{ my: 0.5 }} />
              )}
              <div ref={sentinelRef} />
            </>
          ) : (
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.campusCard.noResults"
              defaultText="No institutions found."
              variant="Inter/Body-sm"
              color="text.secondary"
              sx={{ paddingY: 1.5 }}
            />
          )}
        </Stack>
      )}
    </>
  );
};
